import {
  AddressInput,
  Basket,
  BasketDiscountType, Checkout,
  CheckoutStatus,
  Deal,
  PaymentCurrencyType,
  PaymentOrderStatus, PaymentProvider,
  PaymentStatus,
  WalletEntityType
} from "@irati/types"
import authentication from "@irati/auth"
import paymentService from "@irati/payments/services/Payments"
import walletService from "@irati/wallet/graph/wallet"
import stockService from "@irati/deals/services/stocks"
import {BasketItemType} from "@irati/types/generated/ops"
import tracking from "@irati-tracking/perks/perks"

import basketService from "../../services/basket"
import basket from "../basket"
import checkoutService from "../../services/checkout"
import Screen from "./enum/Screen"
import handleNoProduct, {isNoProduct} from "./flows/handleNoProduct"
import handleSingleProduct, {isSingleProduct} from "./flows/handleSingleProduct"
import checkoutError, {CheckoutError} from "./helpers/checkoutError"

export interface Details {
  screen: Screen
  deal: Deal
  checkoutId?: string
  redemptionId?: string
  paymentOrderId?: string
}

class RedemptionFlow {
  isFlexiRedemption(currency: PaymentCurrencyType) {
    const checkCurrencies = [PaymentCurrencyType.Rp, PaymentCurrencyType.Tt]
    return checkCurrencies.includes(currency)
  }

  async dealInfo(deal: Deal): Promise<Details> {
    if (isNoProduct(deal)) {
      return handleNoProduct(deal)
    }

    // Check stock count if deal has products attached
    await this.checkStockCount(deal)

    // Check for wallet balance (Flexi Perks / Rewards)
    await this.checkWalletBalanceVsMinPrice(deal)

    if (isSingleProduct(deal)) {
      return handleSingleProduct(deal)
    }
    return {screen: Screen.DealSelectProducts, deal}
  }

  async selectProducts(deal: Deal): Promise<Details> {
    const userId = authentication.user().uuid
    const isRewards = deal.productConfig?.products[0]?.salePrice?.currency === PaymentCurrencyType.Rp

    basket.items.forEach((item) => {
      item.targetUserId = userId
      item.amount = item.openValue
      item.entityType = isRewards ? BasketItemType.FlexiReward : BasketItemType.Deal
    })
    const b = await basketService.updateBasket({
      items: basket.items,
      userId,
      paymentDetails: {
        provider: basket.paymentProvider,
        discountType: basket.isCashback ? BasketDiscountType.Cashback : BasketDiscountType.Standard
      }
    })
    await this.isFlexiBalanceEnough(b)
    await basketService.canRedeemBasket(userId, {fetchPolicy: "no-cache"})
    if (b.total.amount > 0) {
      // checkoutService.startCheckout is handled by containers/DealPayment line 57
      return {screen: Screen.DealPayment, deal}
    }

    const checkout = await checkoutService.startCheckout(userId)
    const completeCheckout = await checkoutService.completeCheckout(checkout.uuid)
    const redemptionId = completeCheckout.fullRedemptions[0].uuid
    return {screen: Screen.DealSuccess, deal, checkoutId: checkout.uuid, redemptionId}
  }

  setShippingAddress(shippingAddress: AddressInput): Promise<Basket> {
    const user = authentication.user()
    basket.shippingAddress = shippingAddress
    return basketService.updateBasket({
      items: basket.items,
      userId: user.uuid!,
      shippingAddress
    })
  }

  async paymentComplete(deal: Deal, checkoutId: string, paymentOrderId: string): Promise<Details> {
    try {
      const payment = await paymentService.completePayment({paymentOrderId})
      if (payment.status === PaymentStatus.Failed) {
        throw new Error()
      }
      await this.makeWalletPaymentIfRequired(paymentOrderId, deal, checkoutId)

      const checkout = await checkoutService.completeCheckout(checkoutId)
      if (checkout.status !== CheckoutStatus.Completed) {
        throw new Error()
      }
      const walletAmount = getWalletAmountUsed(checkout)
      tracking.redeemPerkCompleteCheckout(deal, checkout!.paymentOrder?.amount!, checkout!.paymentOrder?.currency!, walletAmount)
      const redemptionId = checkout.fullRedemptions[0].uuid
      return {screen: Screen.DealSuccess, deal, redemptionId, checkoutId}
    } catch (e) {
      const {reason, checkout} = await checkoutError(paymentOrderId, checkoutId)
      tracking.checkoutFailure(getTrackingData(reason, paymentOrderId, checkout))
      return {screen: Screen.DealRedemptionError, deal, checkoutId, paymentOrderId}
    }
  }

  private async makeWalletPaymentIfRequired(paymentOrderId: string, deal: Deal, checkoutId: string): Promise<void> {
    const po = await paymentService.getPaymentOrder(paymentOrderId, {forceRefetch: true})
    if (po.status === PaymentOrderStatus.Pending) {
      let outstanding = po.amount
      po.payments.forEach((p) => {
        if (p.status === "COMPLETED") {
          outstanding -= p.amount
        }
      })
      if (outstanding > 0) {
        const wallet = await walletService.get(authentication.user().uuid, po.currency)
        await paymentService.payByUserWallet(po, wallet, {amount: outstanding}, deal, checkoutId)
      }
    }
  }

  private async checkStockCount(deal: Deal) {
    const {allOutOfStock} = await stockService.stockCount(deal.uuid)
    if (allOutOfStock) {
      throw new Error("perks.containers.DealImportantInfoContainer.noStock.error")
    }
  }

  private async checkWalletBalanceVsMinPrice(deal: Deal): Promise<void> {
    const hasFlexiWallets = authentication.apps("wallet.treatTokens") || authentication.apps("wallet.flexiRewards")
    // Check if deal has price
    let dealMinPrice = deal.productConfig?.limits?.forOpenValue?.minValue
    if (!dealMinPrice) {
      dealMinPrice = deal.productConfig?.products[0]?.salePrice?.value
    }
    const dealCurrency = deal.productConfig?.products[0]?.salePrice?.currency
    // Run balance check just for Rewards and FlexiPerks
    if (!hasFlexiWallets || !dealMinPrice || !dealCurrency || !this.isFlexiRedemption(dealCurrency)) {
      return
    }
    const wallet = await walletService.get(authentication.user().uuid, dealCurrency, WalletEntityType.User, {fetchPolicy: "network-only"})

    // Compare wallet balance with the cheapest product
    if ((wallet?.availableBalance || 0) < dealMinPrice) {
      throw new Error("perks.containers.DealImportantInfoContainer.notEnoughWalletBalance.error")
    }
  }

  private async isFlexiBalanceEnough(b: Basket): Promise<void> {
    const hasFlexiWallets = authentication.apps("wallet.treatTokens") || authentication.apps("wallet.flexiRewards")
    const currency = b?.total?.currency
    // Run balance check just for Rewards and FlexiPerks
    if (!hasFlexiWallets || !currency || !this.isFlexiRedemption(currency)) {
      return
    }
    const wallet = await walletService.get(authentication.user().uuid, currency, WalletEntityType.User, {fetchPolicy: "network-only"})
    if ((wallet?.availableBalanceInCents || 0) < b?.total?.amount) {
      throw new Error("perks.containers.DealImportantInfoContainer.notEnoughWalletBalance.error")
    }
  }
}

const redemptionFlow = new RedemptionFlow()

export default redemptionFlow

const getWalletAmountUsed = (checkout: Checkout) => {
  const walletPayment = checkout!.paymentOrder!.payments!.find((p) => p.provider === PaymentProvider.Wallet)
  return walletPayment?.amount || 0
}

const getTrackingData = (reason: CheckoutError, paymentOrderId: string, checkout?: Checkout) => ({
  reason,
  user_id: authentication.user().uuid,
  tenant_id: authentication.userTenant().uuid,
  payment_id: checkout?.paymentOrder?.payments?.map((payment) => payment.uuid).join(","),
  order_id: paymentOrderId,
  amount: checkout?.paymentOrder?.amount,
  currency: checkout?.paymentOrder?.currency,
  provider: checkout?.paymentOrder?.payments?.map((payment) => payment.provider).join(","),
  country_code: checkout?.paymentOrder?.countryCode,
  deal_id: checkout?.basket?.items?.map((item) => item.deal?.uuid).join(","),
  deal_header: checkout?.basket?.items?.map((item) => item.deal?.header).join(",")
})